import angular from "angular";

import { Ajax, AccountingData, Calculations, DateRange, Calculation, CalculationCorrection, CalculationResultLogic, Reports, VersionInfo, ApiListItem, ApiValidationErrorType, Translations, InputEnumOption, Language, kevaEmploymentRegCodes} from "@salaxy/core";

import { EditDialogKnownActions, InvoicesService, ReportsService, SessionService, UiCrudHelpers, UiHelpers } from "../../services";
import { CalculationCrudController } from "./CalculationCrudController";

/**
 * CalculationEditorController used to handle EPR actions
 * This controller extends ApiCrudObjectController (via CalculationCrudController).
 */
export class CalculationEditorController extends CalculationCrudController {

  /** Bindings for components that use this controller */
  public static bindings = {

  };

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Calculations", "UiHelpers", "UiCrudHelpers", "$location", "$routeParams", "SessionService", "ReportsService", "InvoicesService", "Reports", "AjaxNg1"];


   /** Flags to indicate if the request is in progress */
   public loaders:any = {};

   /** Indicates if the details sidebar is open */
   public isRightSidebarOpen: boolean;

  /** Data reader for the accounting diffa data. */
  public accountingDiffDataReader = {
    /** Function to read data into given array */
    read: (target: string, ruleSet: string, arr: AccountingData[]): Promise<void> => {
      if (this.accountingDiffData) {
        arr.push(this.accountingDiffData);
        return Promise.resolve();
      } else {
        return Promise.resolve();
      }
    },
    /** Optional label for data export */
    exportLabel: null,
    /** Indicates if the export is not possible */
    disabled: () => !this.accountingDiffData,
    /* Indicates if row is expandable */
    showExpand: true,
    /* Indicates if rows exists*/
    hasRows: () => !this.accountingDiffData?.ledgerAccounts
  };

  /** Status for diff data . */
  private accountingDiffDataStatus: "initial" | "loading" | string = "initial";

  private _accountingDiffData: AccountingData;

  private _diffCalc: Calculation = {};

  private latestCurrent: Calculation;

  private latestBase: Calculation;

  private pendingAccountingDiffData = false;

  private _documentFlow: { item: ApiListItem, children: any[]};

  private _state: string;

  private baseCalc: Calculation;
  private versionId: string;

  constructor(
    calcApi: Calculations,
    uiHelpers: UiHelpers,
    protected uiCrudHelpers: UiCrudHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    sessionService: SessionService,
    reportsService: ReportsService,
    protected invoicesService: InvoicesService,
    private reportsApi: Reports,
    private ajax: Ajax,
  ) {
    // Dependency injection
    super(calcApi, reportsService, uiHelpers, $location, $routeParams, sessionService, invoicesService);
    calcApi.getSingle = (id: string) =>  {
      return this.ajax.getJSON(`/v03-rc/api/calculations/correction/${id}`).then((data: CalculationCorrection) => {
        return data.calculation;
      });
    };
  }
  /**
   * Initializes the controller
   */
  public $onInit() {
    super.$onInit();
  }

  /** Calculation total result diff between original and current calculation. */
  public get diffCalc() {
    this.onCalculationChange();
    return this._diffCalc;
  }

  /** Accounting data diff between original and current calculation. */
  public get accountingDiffData() {
    this.onCalculationChange();
    return this._accountingDiffData;
  }

   /** Document flow for the current calculation */
   public get documentFlow() {
    if (!this._documentFlow && this.current?.id) {
      this._documentFlow = { item: null, children: null};
      this.ajax.getJSON(`/v03-rc/api/calculations/correction/${this.current.id}/document-flow`).then((data) => {
        angular.copy(data, this._documentFlow);
      });
    }
    return this._documentFlow;
  }

  /**
   * Returns validation for calculation
   */
  public get validation() {
    // Currently apiValidation isValid is false even if all the items are warnings
    if (!this.current) {
      return null;
    }
    if (!this.current.result.validation.isValid) {
      (this.current.result.validation as any).isValid = this.current.result.validation.errors.every(x => x.type === ApiValidationErrorType.Warning);
    }

    return this.current.result.validation;
  }

  /**
   * Starts the reload process depending on the bindingMode:
   */
   public reload(): Promise<Calculation> {
    return super.reload().then((calculation) => {
      calculation.isReadOnly = false;
      return calculation;
    });
  }
  /** Load accounting data and calculate diff. */
  public loadAccountingDiffData(targetId: string = null) {
    if (targetId == null) {
      if (this.accountingDiffDataStatus === "initial") {
        targetId = "default";
      }
      else if (this.accountingDiffDataStatus === "loading") {
        return;
      }
      else {
        targetId = this.accountingDiffDataStatus;
      }
    }
    if (this.accountingDiffDataStatus !== "loading") {
      this.pendingAccountingDiffData = false;
      // Start downloading for the new id
      this.accountingDiffDataStatus = "loading";

      const promises = [this.base,this.current].map((x) =>  this.reportsApi.getAccountingDataForCalculations([x], targetId, null));
      Promise.all(promises).then( (accountingReports) => {
        if (accountingReports && accountingReports.length == 2) {
          this._accountingDiffData = this.diffAccountingData(accountingReports[0], accountingReports[1]);
        }
        this.accountingDiffDataStatus = targetId;

        if(this.pendingAccountingDiffData){
          this.loadAccountingDiffData();
        }
      });
    }
  }


  /**
   * Change base calculations
   */
   public changeBase() {
    this.uiHelpers.showDialog(
      "salaxy-components/modals/calc/SelectVersion.html",
      null,
      {
        versions: this.versions,
        versionId: this.versionId,
      }).then((result) => {
        if (result && result != "cancel") {
          const version = result as VersionInfo;
          this.api.getVersion(this.currentId, version?.versionId).then((data) => {
            data.isReadOnly = false;
            this.baseCalc = data;
            this.versionId = version?.versionId;
            this.onCalculationChange();
          });
        }
      });
  }


  // TODO: refresh -method should be called after every field update in the ui. Including rows.

  /**
   * Refreshes the calculation using the server side logic.
   * @param refresh Option for refresh.
   */
   public refresh(refresh = "default"){

    let confirmationDescription = "";

    if(refresh == "default"){
      this.loaders[refresh] = true;
        const payload = {
          id : this.current.id,
          calculation: this.current,
          state: this._state,
        };
        this.ajax.postJSON(`/v03-rc/api/calculations/correction/refresh/${refresh}`, payload).then((data) => {
          this._state = data.state;
          data.calculation.isReadOnly = false;
          angular.copy(data.calculation, this.current);
          this.loaders[refresh] = false;
        });
    } else {
      switch (refresh) {
        case "worker":
          confirmationDescription = "Haluatko päivittää työntekijän tiedot laskelmalle?";
          break;
        case "employer":
          confirmationDescription = "Haluatko päivittää työnantajan tiedot laskelmalle?";
          break;
        case "employment":
          confirmationDescription = "Haluatko päivittää työsuhteen tiedot laskelmalle?";
          break;
        case "taxcard":
          confirmationDescription = "Haluatko päivittää verokortin tiedot laskelmalle?";
          break;
        case "insurances":
          confirmationDescription = "Haluatko päivittää vero-, eläke- ja vakuutustiedot laskelmalle?";
          break;
        case "cfa":
          confirmationDescription = "Haluatko päivittää asiakasvaraerät laskelmalle?";
          break;
        default:
          confirmationDescription = "";
      }
      this.uiHelpers.showConfirm("Haluatko jatkaa?", confirmationDescription, "Päivitä", "Peruuta").then((result) =>
    {
      if (result) {
        this.loaders[refresh] = true;
        const payload = {
          id : this.current.id,
          calculation: this.current,
          state: this._state,
        };
        this.ajax.postJSON(`/v03-rc/api/calculations/correction/refresh/${refresh}`, payload).then((data) => {
          this._state = data.state;
          data.calculation.isReadOnly = false;
          angular.copy(data.calculation, this.current);
          this.loaders[refresh] = false;
        });
        }
        else
        {
          return;
        }
      });
    }
  }

  /**
   * Saves the edited readonly calculation.
   */
   public saveEdited(){
    const payload = {
      id : this.current.id,
      calculation: this.current,
      state: this._state,
      snapshot: {
        categories: []
      }
    };
    this.ajax.getJSON("/v03-rc/api/calculations/correction/snapshot/categories").then((categories) =>
    {
      this.uiHelpers.openEditDialog("salaxy-components/modals/calc/EditorConfirm.html", { categories : [""]}, { categories}).then((result) =>
      {
        if (result.action == EditDialogKnownActions.Ok) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.isSaving");
            payload.snapshot = result.item;
            this.ajax.postJSON(`/v03-rc/api/calculations/correction`, payload).then((data) =>
            {
              loading.dismiss();
              this._state = data.state;
              data.calculation.isReadOnly = false;
              angular.copy(data.calculation, this.current);
            });
          }
          else
          {
            return;
          }
        });
    });
  }

  private _dateRange: DateRange;

  /** Getter and setter for Calculation daterange in compatible format for new DateRange component. */
  public get dateRange(): DateRange {
    if (!this.current) {
      return null;
    }
    if (!this._dateRange) {
      this._dateRange = {
        start: this.current.info.workStartDate,
        end: this.current.info.workEndDate,
        daysCount: this.current.framework.numberOfDays,
      };
    }
    return this._dateRange;
  }
  public set dateRange(value: DateRange) {
    this._dateRange = value;
  }

  /**
   * Called when the date range changes.
   */
  public dateRangeChange() {
    if (!this.dateRange || !this.current) {
      return;
    }
    this.current.info.workStartDate = this.dateRange.start;
    this.current.info.workEndDate = this.dateRange.end;
    this.current.framework.numberOfDays = this.dateRange.daysCount;
  }

  private get base() {
    if (!this.baseCalc && this.versions.length > 0) {
      this.versionId = this.versions[this.versions.length - 1].versionId;
      this.baseCalc = angular.copy(this.original);
    }
    return this.baseCalc ?? this.original;
  }

  /**
   * Returns valid employment reg codes for the selected pensions company.
   * @returns Options for the employment reg codes.
   */
  public getEmploymentRegCodeOptions(): Array<InputEnumOption> {
    return this.uiHelpers.cache(this, "employmentRegCodes",
    () => kevaEmploymentRegCodes.filter((x) => x.pensionCompany === this.current.result.responsibilities.pensionCompany).map((regCode) =>
      ({ value: regCode.code, text: Translations.getLanguage() === Language.Sv ? regCode.labelSv : regCode.labelFi })),
    () => this.current.result.responsibilities.pensionCompany);
  }

  private diffAccountingData(originalAccountingData: AccountingData, modifiedAccountingData: AccountingData): AccountingData {
    // Leave only top level.
    originalAccountingData.ledgerAccounts.forEach((x) => x.entries = []);
    originalAccountingData.summary = {};
    modifiedAccountingData.ledgerAccounts.forEach((x) => x.entries = []);
    modifiedAccountingData.summary = {};
    const result = modifiedAccountingData;

    originalAccountingData.ledgerAccounts.forEach( (orig) => {
      const modified =  result.ledgerAccounts.find((x) => x.accountNumber === orig.accountNumber && x.accountGrouping == orig.accountGrouping );
      if (modified) {
        (modified as any).saldo = modified.saldo - orig.saldo;
      } else {
        const removedAccount = angular.copy(orig);
        (removedAccount as any).saldo = 0.0 - orig.saldo;
        result.ledgerAccounts.push(removedAccount);
      }
    });
    result.ledgerAccounts.forEach((x) => x.entries =
    [
      {
        id: null,
        entryDate: null,
        description: "Ero",
        isDebit: x.saldo >= 0,
        amount: x.saldo >= 0 ? x.saldo : -1.0 * x.saldo,
        vatPercent: null,
        accountNumber: x.accountNumber,
        accountName: x.accountName,
        accountGrouping: x.accountGrouping,
        dimension: {},
      }
    ]);
    return result;
  }

  private onCalculationChange(){
    if (this.base && this.current && ( !angular.equals(this.current, this.latestCurrent) || !angular.equals(this.base, this.latestBase))) {
      this.latestCurrent = angular.copy(this.current);
      this.latestBase = angular.copy(this.base);
      const diff = angular.copy(this.base);
      diff.result = CalculationResultLogic.diff(this.base.result, this.current.result);
      angular.copy(diff, this._diffCalc);
      this.pendingAccountingDiffData = true;
      this.loadAccountingDiffData();
    }
  }

}
